// Shared types and tables of the admin panel.
//
// The capabilities (CapId) and the action -> capability table mirror
// skymp5-server/ts/adminTiers.ts (ADMIN_CAPS, ACTION_CAP). The server refuses
// what a person may not do anyway; the panel only uses this to show the right
// menus and actions, so a drift between the two costs a refused action at most.

export type CapId =
  | 'players' | 'teleport' | 'modes' | 'items' | 'kick' | 'ban' | 'broadcast' | 'world' | 'restart' | 'weather'
  | 'reports' | 'housing' | 'debug' | 'console' | 'staff';

export const ALL_CAPS: CapId[] = [
  'players', 'teleport', 'modes', 'items', 'kick', 'ban', 'broadcast', 'world', 'restart', 'weather',
  'reports', 'housing', 'debug', 'console', 'staff',
];

// Same wording as the server's CAP_INFO (staffSystem.ts); used where the
// server's own capInfo packet is not available (the identity popover).
export const CAP_LABELS: Record<CapId, { label: string; description: string }> = {
  players: { label: 'Joueurs', description: 'Soigner, geler, ressusciter, invoquer, rangs, métiers, emplacements de personnage, apparence.' },
  teleport: { label: 'Téléportation', description: 'Se téléporter aux lieux enregistrés.' },
  modes: { label: 'Modes', description: 'Invisibilité, mode dieu, traversée des murs et autres modes admin.' },
  items: { label: 'Objets et or', description: "Donner ou poser des objets, créer de l'or, vider un inventaire." },
  kick: { label: 'Expulsions', description: 'Expulser un joueur du serveur.' },
  ban: { label: 'Bannissements', description: 'Bannir et débannir.' },
  broadcast: { label: 'Annonces', description: 'Diffuser un message à tous les joueurs.' },
  world: { label: 'Monde et placement', description: 'Placer ou retirer des stations, boîtes aux lettres, objets, et masquer des objets de la carte.' },
  restart: { label: 'Redémarrage', description: 'Lancer ou annuler un compte à rebours de redémarrage.' },
  weather: { label: 'Météo et heure', description: "Changer la météo et l'heure du jeu." },
  reports: { label: 'Signalements', description: 'Lire et traiter les signalements des joueurs.' },
  housing: { label: 'Logements', description: 'Gérer les logements et leurs propriétaires.' },
  debug: { label: 'Debug', description: 'Outils de diagnostic.' },
  console: { label: 'Commandes en jeu', description: 'Les commandes du chat et de la console réservées au staff.' },
  staff: { label: 'Gestion du staff', description: 'Réservée aux propriétaires (fichier de configuration du serveur).' },
};

// Cap each panel action needs (adminTiers.ts's ACTION_CAP). openItemGiverFor is
// not in the server table (it is the openItemGiver packet): it hands out items.
export const ACTION_CAP: Record<string, CapId | null> = {
  teleportTo: 'players',
  summon: 'players',
  revive: 'players',
  reviveZone: 'players',
  heal: 'players',
  freeze: 'players',
  wipeInventory: 'items',
  giveGold: 'items',
  openItemGiverFor: 'items',
  openRaceMenu: 'players',
  grantExtraSlot: 'players',
  revokeExtraSlot: 'players',
  teleportLoc: 'teleport',
  toggleMode: 'modes',
  toggleModeForTarget: 'modes',
  kick: 'kick',
  ban: 'ban',
  unban: 'ban',
  broadcastMessage: 'broadcast',
  weatherList: 'weather',
  weatherSet: 'weather',
  weatherClear: 'weather',
  timeSet: 'weather',
  timeShift: 'weather',
  timeReset: 'weather',
};

export interface PanelPlayer {
  a?: string; // actor/form id hex
  p: number; // profileId
  n: string; // character name
  d: string; // discordId
  ip: string; // masked server-side
  hwid: string;
  online: boolean;
  ping: number | null;
  prof: string; // "Mineur (Apprenti), Archer (Novice, spé)"
  slot2: boolean; // whether the second character slot is unlocked
  banned?: boolean;
}

export interface PanelLocation {
  name: string;
}

export interface PanelMode {
  id: string;
  label: string;
  active: boolean;
}

export interface PanelHouse {
  refrId: number;
  displayName: string;
  holdId: string | null;
  holdName: string | null;
  ownerName: string | null;
  memberCount: number;
}

export interface PanelReport {
  id: string;
  reporterName: string;
  text: string;
  cellOrWorldDesc: string;
  createdAt: number;
  status: 'open' | 'resolved' | 'dismissed';
  resolvedByName?: string;
  resolvedAt?: number;
}

export interface AdminPanelDetail {
  players: PanelPlayer[];
  locations: PanelLocation[];
  modes: PanelMode[];
  reports: PanelReport[];
  houses: PanelHouse[];
  me: { caps: CapId[] };
}

export interface ServerStatus {
  tps: number | null;
  onlinePlayers: number;
  uptimeSec: number;
  // Not sent by the server today: shown as a pill and an overview card only when it is
  restartInSec?: number | null;
}

export interface DebugTarget {
  name: string;
  refId: string;
  refDesc: string | null;
  baseId: string;
  baseDesc: string | null;
  cell: string;
  cellName: string;
  pos: number[];
  dist: number;
  live: boolean;
}

export interface WeatherEntry {
  editorId: string;
  formId: string; // hex string, "0x81a"
  category: string;
}

export interface ClockTimeView {
  hour: number;
  minute: number;
  offsetMinutes: number;
  adjusted: boolean;
}

export interface WeatherView {
  current: { formId: number; editorId: string; forced: boolean } | null;
  list: WeatherEntry[];
  time: ClockTimeView | null;
}

export interface StaffRole {
  id: string;
  name: string;
  description: string;
  color: string; // "#rrggbb"
  caps: CapId[];
  builtin: boolean;
}

export interface StaffMemberView {
  discordId: string;
  name: string;
  roleId: string | null;
  // Permissions added on top of the role
  caps: CapId[];
  // Permissions taken away from the role
  denied: CapId[];
  note: string;
  // false: suspended, no access at all
  active: boolean;
  // Epoch ms, null for a permanent access
  expiresAt: number | null;
  addedBy: string;
  addedAt: number;
  updatedAt: number;
  // 0 when the person never connected
  lastSeenAt: number;
  // Computed by the server: (role.caps + caps) - denied, empty when suspended or expired
  effectiveCaps: CapId[];
  expired: boolean;
  characters: string[];
  online: boolean;
}

export interface StaffOwnerView {
  discordId: string;
  characters: string[];
  online: boolean;
}

export interface AuditEntry {
  at: number;
  by: string;
  action: string;
  target: string;
  detail: string;
}

export interface StaffState {
  canManage: boolean;
  owners: StaffOwnerView[];
  members: StaffMemberView[];
  roles: StaffRole[];
  capInfo: Array<{ id: CapId; label: string; description: string }>;
  audit: AuditEntry[];
}

export interface StaffAudit {
  discordId: string | null;
  entries: AuditEntry[];
}

export interface StaffCandidate {
  name: string;
  discordId: string | null;
  online: boolean;
  profileId: number;
}

export type ViewId =
  | 'overview' | 'players' | 'modes' | 'reports' | 'broadcast' | 'teleport' | 'placement' | 'spawns'
  | 'weather' | 'housing' | 'status' | 'debug' | 'staff';

export interface NavItem {
  id: ViewId;
  label: string;
  group: string;
  cap: CapId | null; // null: every staff member
  hint: string;
  icon: string;
}

export const NAV_GROUPS = ["Vue d'ensemble", 'Joueurs', 'Modération', 'Monde', 'Serveur', 'Staff'];

export const NAV: NavItem[] = [
  { id: 'overview', label: 'Accueil', group: "Vue d'ensemble", cap: null, hint: 'Synthèse et actions rapides', icon: 'home' },
  { id: 'players', label: 'Joueurs', group: 'Joueurs', cap: 'players', hint: 'Gestion des personnages', icon: 'users' },
  { id: 'modes', label: 'Modes', group: 'Joueurs', cap: 'modes', hint: 'Vos modes admin', icon: 'modes' },
  { id: 'reports', label: 'Signalements', group: 'Modération', cap: 'reports', hint: 'Signalements des joueurs', icon: 'reports' },
  { id: 'broadcast', label: 'Annonces', group: 'Modération', cap: 'broadcast', hint: 'Message à tous les joueurs', icon: 'broadcast' },
  { id: 'teleport', label: 'Téléportation', group: 'Monde', cap: 'teleport', hint: 'Lieux enregistrés', icon: 'teleport' },
  { id: 'placement', label: 'Placement', group: 'Monde', cap: 'world', hint: 'Objets, stations, carte', icon: 'placement' },
  { id: 'spawns', label: 'Spawns', group: 'Monde', cap: 'world', hint: 'Zones de créatures et donjons', icon: 'spawns' },
  { id: 'weather', label: 'Météo et heure', group: 'Monde', cap: 'weather', hint: 'Ciel et horloge du jeu', icon: 'weather' },
  { id: 'housing', label: 'Logements', group: 'Monde', cap: 'housing', hint: 'Logements occupés', icon: 'housing' },
  { id: 'status', label: 'Statut', group: 'Serveur', cap: null, hint: 'Santé du serveur', icon: 'status' },
  { id: 'debug', label: 'Debug', group: 'Serveur', cap: 'debug', hint: "Identité de ce que l'on vise", icon: 'debug' },
  { id: 'staff', label: 'Gestion du staff', group: 'Staff', cap: 'staff', hint: 'Membres et permissions', icon: 'staff' },
];
